import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { reviewsRoutes } from "@redotech/redo-merchant-review-app/route";
import { matchPath, Navigate } from "react-router-dom";
import {
  MARKETING_ANALYTICS_TABS_URL_PARAM,
  MarketingAnalyticsPage,
} from "../../../analytics/marketing/marketing-analytics";
import {
  automationRoutes,
  backwardsCompatibleEmailSmsRoutes,
  emailSmsRoutes,
  loyaltyRoutes,
} from "../../../marketing/route";

const HIDE_PADDING_ROUTES: string[] = [
  "/reviews/all-reviews",
  "/reviews/products",
  "/email-sms/audience",
  "/email-sms/audience/customers/:customerId",
  "/automations/*",
  "/email-sms/campaigns/*",
  "/email-sms/segments/*",
  "/email-sms/templates/email/*",
  "/email-sms/templates/sms/*",
  "/email-sms/templates/click-map/*",
  "/email-sms/campaigns/:campaignId/details",
  "/email-sms/signups/*",
  "/email-sms/ai-personas/*",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/members",
  "/loyalty/page/customize",
  "/tracking/tracking-page/tracking-page-builder",
  "/tracking/tracking-page/edit-flow",
  "/tracking/orders",
  "/tracking/shipments",
  "/tracking/shipments/:viewName",
  "/email-sms/deliverability/*",
  "/recover/conversation-log",
  "/marketing-agent/conversations",
  "/marketing-agent",
];

function hidePadding(url: string, search: { search: string }): boolean {
  if (
    url.includes("/marketing/analytics") &&
    new URLSearchParams(search.search).get(
      MARKETING_ANALYTICS_TABS_URL_PARAM,
    ) === MarketingAnalyticsPage.BACK_IN_STOCK
  ) {
    return true;
  }

  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/upsell-page/:id",
  "/email-sms/signups/builder/:id",
  "/email-sms/templates/email/:id",
  "/email-sms/templates/sms/:id",
  "/email-sms/templates/click-map/:templateId",
  "/email-sms/segments/:segmentId",
  "/email-sms/campaigns/:campaignId/edit/*",
  "/email-sms/campaigns/:campaignId/edit-with-ai/template",
  "/automations/:automationId",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/page/customize",
  "/tracking/tracking-page/tracking-page-builder",
  "/tracking/tracking-page/edit-flow",
];

const HIDE_HEADER_ROUTES: string[] = ["/tracking/about", ...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

export const marketingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Marketing" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="email-sms" /> },
      {
        path: "welcome",
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { MarketingWelcomePage } = await import(
            "@redotech/redo-merchant-marketing-app/welcome/marketing-welcome-page"
          );
          return { Component: MarketingWelcomePage };
        },
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        async lazy() {
          const { MarketingAnalytics } = await import(
            "@redotech/redo-merchant-app/analytics/marketing/marketing-analytics"
          );
          return { Component: MarketingAnalytics };
        },
      },
      {
        path: "marketing-agent",
        handle: { breadcrumb: "Marketing Agent" },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingAgentPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-page"
              );
              return { Component: MarketingAgentPage };
            },
          },
          {
            path: "dashboard",
            handle: { breadcrumb: "Dashboard" },
            async lazy() {
              const { MarketingAgentDashboardPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/pages/dashboard/page"
              );
              return { Component: MarketingAgentDashboardPage };
            },
          },
          {
            path: "analytics",
            handle: { breadcrumb: "Analytics" },
            async lazy() {
              const { MarketingAgentAnalyticsPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/pages/analytics/page"
              );
              return { Component: MarketingAgentAnalyticsPage };
            },
          },
          {
            path: "conversations",
            handle: { breadcrumb: "Conversations" },
            async lazy() {
              const { MarketingAgentConversationsPage } = await import(
                "@redotech/redo-merchant-marketing-app/marketing-agent/pages/conversations/page"
              );
              return { Component: MarketingAgentConversationsPage };
            },
          },
        ],
      },
      { path: "reviews", children: reviewsRoutes },
      { path: "loyalty", children: loyaltyRoutes },
      {
        path: "recover",
        children: [
          {
            path: "welcome",
            handle: { breadcrumb: "Welcome" },
            async lazy() {
              const { RecoverWelcomePage } = await import(
                "@redotech/redo-merchant-marketing-app/recover/recover-welcome-page"
              );
              return { Component: RecoverWelcomePage };
            },
          },
          {
            path: "analytics",
            handle: { breadcrumb: "Analytics" },
            async lazy() {
              const { RecoverAnalyticsPage } = await import(
                "@redotech/redo-merchant-marketing-app/recover/recover-analytics-page"
              );
              return { Component: RecoverAnalyticsPage };
            },
          },
          {
            path: "conversation-log",
            handle: { breadcrumb: "Conversation log" },
            async lazy() {
              const { RecoverConversationLogPage } = await import(
                "@redotech/redo-merchant-marketing-app/recover/recover-conversation-log-page"
              );
              return { Component: RecoverConversationLogPage };
            },
          },
        ],
      },
      {
        path: "tracking",
        children: [
          {
            index: true,
            lazy: async () => {
              const { OrderTrackingLandingPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/order-tracking-landing-page"
              );
              return { Component: OrderTrackingLandingPage };
            },
          },
          {
            path: "about",
            handle: { breadcrumb: "Tracking", breadcrumbNavigate: false },
            async lazy() {
              const { AboutTracking } = await import(
                "@redotech/redo-merchant-app/setting/about-tracking/about-tracking"
              );
              return { element: <AboutTracking /> };
            },
          },
          {
            path: "orders",
            handle: { breadcrumb: "Orders" },
            lazy: async () => {
              const { Orders } = await import(
                "@redotech/redo-merchant-app/order/orders-table/orders-table"
              );
              return {
                element: (
                  <Orders
                    filterParams={(filters) => ({
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      dateRange: filters.dateRange,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      shipmentStatus: filters.shipmentStatus,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      carrier: filters.carrier,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      destinationState: filters.destinationState,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      destinationCountry: filters.destinationCountry,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      originState: filters.originState,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      originCountry: filters.originCountry,
                      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                      dateFilter: filters.dateFilter,
                    })}
                  />
                ),
              };
            },
          },
          {
            path: "shipments",
            handle: { breadcrumb: "Shipments" },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ShipmentsPageWithActiveView } = await import(
                    "@redotech/redo-merchant-marketing-app/order-tracking/shipments/shipments-page"
                  );
                  return { Component: ShipmentsPageWithActiveView };
                },
              },
              {
                path: ":viewName",
                lazy: async () => {
                  const { ShipmentsPageWithActiveView } = await import(
                    "@redotech/redo-merchant-marketing-app/order-tracking/shipments/shipments-page"
                  );
                  return { Component: ShipmentsPageWithActiveView };
                },
              },
            ],
          },
          {
            path: "tracking-page/tracking-page-builder",
            lazy: async () => {
              const { TrackingPageBuilderPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page-builder"
              );
              return { Component: TrackingPageBuilderPage };
            },
          },
          {
            path: "tracking-page/edit-flow",
            lazy: async () => {
              const { EditTrackingPageFlowPage } = await import(
                "../../../setting/tracking-page-flow"
              );
              return { Component: EditTrackingPageFlowPage };
            },
          },
          {
            path: "tracking-page",
            handle: { breadcrumb: "Tracking page" },
            lazy: async () => {
              const { TrackingPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page"
              );
              return { Component: TrackingPage };
            },
          },
        ],
      },
      { path: "email-sms", children: emailSmsRoutes },
      { path: "automations", children: automationRoutes },
      {
        path: "email-builder/click-map/:templateId",
        handle: { breadcrumb: "Click Map" },
        async lazy() {
          const { FullscreenClickMap } = await import(
            "@redotech/redo-merchant-marketing-app/email-builder/click-map/fullscreen-click-map"
          );
          return { Component: FullscreenClickMap };
        },
      },
      ...backwardsCompatibleEmailSmsRoutes,
    ],
  },
];

export const backwardsCompatibleMarketingRoutes: PermissionedRouteObject[] = [
  {
    path: "reviews",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "tracking",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
        ],
      },
    ],
  },
];
